import { useState } from 'react'
import {
  Card, Table, Button, Tag, Space, Typography, Form, Select, DatePicker,
  Input, InputNumber, Switch, Row, Col, Drawer, message,
} from 'antd'
import { PlusOutlined, EditOutlined, ExperimentOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { qualityTestsApi } from '@/features/reports/api'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title } = Typography

const SAMPLE_TYPES = [
  'FIBRE_HVI', 'CARD_SLIVER', 'DRAW_SLIVER', 'COMBED_SLIVER',
  'ROVING', 'YARN_RING', 'YARN_CONE', 'SPLICE',
]

// Departments commonly cited as the sample source. Free-text used to
// allow typos like "ring spining" → broken aggregations downstream.
const DEPARTMENTS = [
  'BLOWROOM', 'CARDING', 'BREAKER_DRAW', 'FINISHER_DRAW',
  'COMBING', 'ROVING', 'RING_SPINNING', 'AUTO_WINDING', 'TFO',
]

export default function QualityTestPage() {
  const { plantId } = usePlantId()
  const qc = useQueryClient()

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null)
  const [filterSampleType, setFilterSampleType] = useState<string | undefined>()
  const [filterResult, setFilterResult] = useState<string | undefined>()
  const [form] = Form.useForm()

  const { data: tests, isLoading } = useQuery({
    queryKey: ['quality-tests-list', plantId, filterSampleType, filterResult],
    queryFn: () => qualityTestsApi.list({ plant_id: plantId, sample_type: filterSampleType, result: filterResult, limit: 200 }),
    enabled: !!plantId,
  })

  // Active mill lots — used as typeahead options for lot_no so users
  // pick an existing lot instead of typing a freeform string. Falls
  // back to free-text input behaviour because lot_no on the test
  // schema is a string, not an FK.
  const { data: millLots = [] } = useQuery({
    queryKey: ['mill-lots-for-qc', plantId],
    queryFn: async () => {
      const r = await apiClient.get('/bale-mgmt/mill-lots', {
        params: { plant_id: plantId, size: 200 },
      })
      return Array.isArray(r.data) ? r.data : (r.data?.items ?? [])
    },
    enabled: !!plantId && open,
  })

  const lotOptions = millLots.map((l: any) => ({
    value: l.lot_no ?? l.mill_lot_no ?? l.id,
    label: `${l.lot_no ?? l.mill_lot_no ?? '—'}${l.variety ? ` · ${l.variety}` : ''}${l.crop_year ? ` · ${l.crop_year}` : ''}`,
  }))

  const createMut = useMutation({
    mutationFn: (data: Record<string, unknown>) => qualityTestsApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quality-tests-list'] })
      qc.invalidateQueries({ queryKey: ['quality-tests'] })
      message.success('Quality test recorded')
      setOpen(false)
      form.resetFields()
    },
    onError: (e: unknown) => message.error((e as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Save failed'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      qualityTestsApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quality-tests-list'] })
      message.success('Quality test updated')
      setOpen(false)
      setEditing(null)
      form.resetFields()
    },
    onError: (e: unknown) => message.error((e as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Update failed'),
  })

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({ test_date: dayjs(), result: 'PASS', deviation_raised: false })
    setOpen(true)
  }

  const openEdit = (record: Record<string, unknown>) => {
    setEditing(record)
    form.setFieldsValue({
      ...record,
      test_date: record.test_date ? dayjs(record.test_date as string) : undefined,
    })
    setOpen(true)
  }

  const handleSubmit = () => {
    form.validateFields().then(vals => {
      const payload = {
        ...vals,
        plant_id: plantId,
        test_date: vals.test_date?.toISOString(),
      }
      if (editing) {
        updateMut.mutate({ id: editing.id as string, data: payload })
      } else {
        createMut.mutate(payload)
      }
    })
  }

  const columns = [
    { title: 'Test No', dataIndex: 'test_no', key: 'no', width: 120 },
    { title: 'Date', dataIndex: 'test_date', key: 'date', width: 130,
      render: (v: string) => v ? dayjs(v).format('DD-MMM-YYYY') : '' },
    { title: 'Sample Type', dataIndex: 'sample_type', key: 'type', width: 130,
      render: (v: string) => v?.replace('_', ' ') },
    { title: 'Count (Ne)', dataIndex: 'count_ne', key: 'count_ne', width: 90, align: 'center' as const },
    { title: 'U%', dataIndex: 'u_pct', key: 'u', width: 80, align: 'right' as const },
    { title: 'Strength (RKm)', dataIndex: 'strength_rkm', key: 'str', width: 120, align: 'right' as const },
    { title: 'Neps', dataIndex: 'neps', key: 'neps', width: 70, align: 'right' as const },
    { title: 'Micronaire', dataIndex: 'micronaire', key: 'mic', width: 90, align: 'right' as const },
    { title: 'SCI', dataIndex: 'sci', key: 'sci', width: 80, align: 'right' as const },
    {
      title: 'Result', dataIndex: 'result', key: 'result', width: 90,
      render: (v: string) => (
        <Tag color={v === 'PASS' ? 'green' : v === 'FAIL' ? 'red' : v === 'HOLD' ? 'orange' : 'blue'}>{v}</Tag>
      )
    },
    { title: 'Uster Grade', dataIndex: 'uster_grade', key: 'grade', width: 90, align: 'center' as const },
    {
      title: 'Actions', key: 'actions', width: 80,
      render: (_: unknown, record: Record<string, unknown>) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
      ),
    },
  ]

  const selectedType = Form.useWatch('sample_type', form)
  const isHVI = selectedType === 'FIBRE_HVI'
  const isYarn = ['YARN_RING', 'YARN_CONE', 'CARD_SLIVER', 'DRAW_SLIVER', 'COMBED_SLIVER', 'ROVING'].includes(selectedType)
  const isSplice = selectedType === 'SPLICE'

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <ExperimentOutlined style={{ marginRight: 8, color: '#52c41a' }} />
        Quality Lab Tests
      </Title>

      <Card style={{ marginTop: 16 }}>
        <Row justify="space-between" style={{ marginBottom: 16 }}>
          <Space>
            <Select
              allowClear placeholder="Sample Type" style={{ width: 160 }}
              onChange={setFilterSampleType}
            >
              {SAMPLE_TYPES.map(t => <Select.Option key={t} value={t}>{t.replace('_', ' ')}</Select.Option>)}
            </Select>
            <Select
              allowClear placeholder="Result" style={{ width: 110 }}
              onChange={setFilterResult}
            >
              {['PASS', 'FAIL', 'HOLD', 'RETEST'].map(r => <Select.Option key={r} value={r}>{r}</Select.Option>)}
            </Select>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Record Test
          </Button>
        </Row>
        <Table
          dataSource={Array.isArray(tests) ? tests : []}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 1200 }}
          size="small"
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <Drawer
        title={editing ? 'Edit Quality Test' : 'Record Quality Test'}
        open={open}
        onClose={() => { setOpen(false); setEditing(null); form.resetFields() }}
        width={640}
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => { setOpen(false); setEditing(null); form.resetFields() }}>Cancel</Button>
            <Button
              type="primary"
              loading={createMut.isPending || updateMut.isPending}
              onClick={handleSubmit}
            >
              Save
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="test_date" label="Test Date" rules={[{ required: true }]}>
                <DatePicker showTime style={{ width: '100%' }} format="DD-MMM-YYYY HH:mm" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="sample_type" label="Sample Type" rules={[{ required: true }]}>
                <Select>
                  {SAMPLE_TYPES.map(t => <Select.Option key={t} value={t}>{t.replace('_', ' ')}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lot_no" label="Lot No">
                <Select
                  showSearch
                  allowClear
                  placeholder={lotOptions.length === 0 ? 'No mill lots — type free-text' : 'Pick or type a lot'}
                  options={lotOptions}
                  optionFilterProp="label"
                  // Allow free-text fallback (sample types like YARN_*
                  // reference yarn lots, not always mill lots).
                  filterOption={(input, option) =>
                    (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  // mode="tags" so the user can add a custom value
                  mode="tags"
                  maxCount={1}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="from_department" label="From Department">
                <Select
                  allowClear
                  placeholder="Pick the source department"
                  options={DEPARTMENTS.map(d => ({ value: d, label: d.replace(/_/g, ' ') }))}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Yarn / Sliver Parameters */}
          {isYarn && (
            <>
              <Card size="small" title="Yarn / Sliver Parameters" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  {[
                    ['count_actual', 'Count Actual (Ne)'],
                    ['count_cv_pct', 'Count CV %'],
                    ['u_pct', 'U %'],
                    ['cvm_pct', 'CVM %'],
                    ['hairiness', 'Hairiness'],
                    ['strength_rkm', 'Strength (RKm)'],
                    ['elongation_pct', 'Elongation %'],
                    ['twist_per_m', 'Twist/m'],
                    ['twist_cv_pct', 'Twist CV %'],
                  ].map(([key, label]) => (
                    <Col span={8} key={key}>
                      <Form.Item name={key} label={label}>
                        <InputNumber style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  ))}
                </Row>
                <Row gutter={16}>
                  {[
                    ['thin_places', 'Thin Places'],
                    ['thick_places', 'Thick Places'],
                    ['neps', 'Neps'],
                  ].map(([key, label]) => (
                    <Col span={8} key={key}>
                      <Form.Item name={key} label={label}>
                        <InputNumber style={{ width: '100%' }} precision={0} />
                      </Form.Item>
                    </Col>
                  ))}
                </Row>
              </Card>
            </>
          )}

          {/* HVI Fibre Parameters */}
          {isHVI && (
            <Card size="small" title="HVI Fibre Parameters" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                {[
                  ['micronaire', 'Micronaire'],
                  ['staple_length_mm', 'Staple Length (mm)'],
                  ['uniformity_index', 'Uniformity Index'],
                  ['short_fibre_index', 'SFI'],
                  ['fibre_strength_gnTex', 'Fibre Strength (gn/tex)'],
                  ['elongation_fibre_pct', 'Elongation %'],
                  ['rd_brightness', 'Rd (Brightness)'],
                  ['b_yellowness', '+b (Yellowness)'],
                  ['trash_pct', 'Trash %'],
                  ['trash_count', 'Trash Count'],
                ].map(([key, label]) => (
                  <Col span={8} key={key}>
                    <Form.Item name={key} label={label}>
                      <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                ))}
              </Row>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                SCI is auto-calculated from: -414.67 + 2.9×Strength - 9.32×Mic + 49.17×Length + 4.74×Uniformity + 0.65×Rd + 0.36×(+b)
              </Typography.Text>
            </Card>
          )}

          {/* Splice Parameters */}
          {isSplice && (
            <Card size="small" title="Splice Test Parameters" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="splice_strength_pct" label="Splice Strength %">
                    <InputNumber style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="splice_appearance" label="Splice Appearance">
                    <Select>
                      {['GOOD', 'ACCEPTABLE', 'POOR'].map(v => <Select.Option key={v} value={v}>{v}</Select.Option>)}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          )}

          {/* Result */}
          <Card size="small" title="Result">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="result" label="Result" rules={[{ required: true }]}>
                  <Select>
                    {['PASS', 'FAIL', 'HOLD', 'RETEST'].map(r => <Select.Option key={r} value={r}>{r}</Select.Option>)}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="uster_grade" label="Uster Grade">
                  <Select allowClear>
                    {['A', 'B', 'C', 'D', 'E'].map(g => <Select.Option key={g} value={g}>{g}</Select.Option>)}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="deviation_raised" label="Deviation Raised" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="remarks" label="Remarks">
                  <Input.TextArea rows={2} />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Form>
      </Drawer>
    </div>
  )
}
